import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { displayClientName } from "../../utils/clientName";
import { PERIOD_LABEL } from "../../pages/Subscriptions";

const PERIOD_MONTHS = { monthly: 1, quarterly: 3, semiannual: 6, annual: 12 };

const useStyles = makeStyles((theme) => ({
  preview: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(148,163,184,0.10)"
        : theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
  },
  mrr: { fontSize: "1.3rem", fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  hint: { color: theme.palette.text.secondary, display: "block" },
}));

const money = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const SubscriptionModal = ({ open, onClose, subscriptionId, onSaved }) => {
  const classes = useStyles();
  const [form, setForm] = useState({
    clientId: "",
    planId: "",
    amount: "",
    quantity: 1,
    billingPeriod: "monthly",
    startedAt: new Date().toISOString().slice(0, 10),
  });
  const [clients, setClients] = useState([]);
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);

  const isEdit = !!subscriptionId;

  useEffect(() => {
    if (!open) return;
    api.get("/clients", { params: { pageSize: 200 } })
      .then((r) => setClients(r.data.clients || []))
      .catch(() => setClients([]));
    api.get("/subscription-plans")
      .then((r) => setPlans(r.data || []))
      .catch(() => setPlans([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!subscriptionId) {
      setForm({
        clientId: "",
        planId: "",
        amount: "",
        quantity: 1,
        billingPeriod: "monthly",
        startedAt: new Date().toISOString().slice(0, 10),
      });
      return;
    }
    api.get(`/subscriptions/${subscriptionId}`)
      .then(({ data }) =>
        setForm({
          clientId: data.clientId,
          planId: data.planId || "",
          amount: data.amount,
          quantity: data.quantity,
          billingPeriod: data.billingPeriod,
          startedAt: data.startedAt,
        })
      )
      .catch(toastError);
  }, [open, subscriptionId]);

  // Ao escolher um plano, o valor vem preenchido — mas continua editável,
  // porque o negociado é que vale.
  const handlePlan = (e) => {
    const planId = e.target.value;
    const plan = plans.find((p) => p.id === Number(planId));
    setForm((prev) => ({
      ...prev,
      planId,
      amount: plan ? plan.amount : prev.amount,
      billingPeriod: plan ? plan.billingPeriod : prev.billingPeriod,
    }));
  };

  const months = PERIOD_MONTHS[form.billingPeriod] || 1;
  const mrrPreview =
    ((Number(form.amount) || 0) * (Number(form.quantity) || 1)) / months;

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        amount: Number(form.amount) || 0,
        quantity: Number(form.quantity) || 1,
        billingPeriod: form.billingPeriod,
      };
      if (isEdit) {
        await api.put(`/subscriptions/${subscriptionId}`, payload);
      } else {
        await api.post("/subscriptions", {
          ...payload,
          clientId: Number(form.clientId),
          planId: form.planId ? Number(form.planId) : null,
          startedAt: form.startedAt,
        });
      }
      toast.success(isEdit ? "Assinatura atualizada" : "Assinatura criada");
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  }, [form, isEdit, subscriptionId, onSaved, onClose]);

  const canSave = (isEdit || form.clientId) && Number(form.amount) > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? "Editar assinatura" : "Nova assinatura"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ marginTop: 4 }}>
          {!isEdit && (
            <>
              <Grid item xs={12} sm={7}>
                <TextField
                  select
                  fullWidth
                  label="Cliente *"
                  value={form.clientId}
                  onChange={(e) => setForm({ ...form, clientId: e.target.value })}
                >
                  <MenuItem value="">—</MenuItem>
                  {clients.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {displayClientName(c)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  type="date"
                  label="Início"
                  InputLabelProps={{ shrink: true }}
                  value={form.startedAt}
                  onChange={(e) => setForm({ ...form, startedAt: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Plano (opcional)"
                  value={form.planId}
                  onChange={handlePlan}
                >
                  <MenuItem value="">Sem plano — valor avulso</MenuItem>
                  {plans.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name} — {money(p.amount)} / {PERIOD_LABEL[p.billingPeriod]}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography variant="caption" className={classes.hint}>
                  O plano preenche o valor, mas o negociado é que vale — pode ajustar abaixo.
                </Typography>
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              type="number"
              label="Valor por ciclo *"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              select
              fullWidth
              label="Periodicidade"
              value={form.billingPeriod}
              onChange={(e) => setForm({ ...form, billingPeriod: e.target.value })}
            >
              {Object.entries(PERIOD_LABEL).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              type="number"
              label="Quantidade"
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <div className={classes.preview}>
              <Typography variant="caption" className={classes.hint}>
                MRR equivalente
              </Typography>
              <div className={classes.mrr}>{money(mrrPreview)}</div>
              <Typography variant="caption" className={classes.hint}>
                {money(form.amount)} × {form.quantity || 1} ÷ {months}{" "}
                {months === 1 ? "mês" : "meses"} — é assim que entra no total de MRR.
              </Typography>
            </div>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button color="primary" variant="contained" onClick={handleSave} disabled={saving || !canSave}>
          {saving ? "Salvando…" : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SubscriptionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  subscriptionId: PropTypes.number,
  onSaved: PropTypes.func,
};

SubscriptionModal.defaultProps = { subscriptionId: null, onSaved: undefined };

export default SubscriptionModal;
